import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';
import FluxoEditor from '../components/fluxos/FluxoEditor';

const FLUXO_LIST_CSS = `
.fluxo-list .text-muted { color: var(--portal-muted) !important; }
.fluxo-list .fluxo-btn-primary {
  background: linear-gradient(135deg, var(--portal-blue) 0%, var(--portal-blue-strong) 100%);
  color: #fff; font-weight: 600; border: none; padding: 8px 18px; border-radius: 12px;
}
.fluxo-list .fluxo-btn-primary:hover { filter: brightness(1.08); }
.fluxo-list .fluxo-card {
  background: var(--portal-surface); border: 1px solid var(--portal-border);
  border-radius: 16px; padding: 6px 14px; box-shadow: var(--portal-shadow);
}
.fluxo-list .fluxo-empty {
  background: var(--portal-surface); border: 1px dashed var(--portal-border); border-radius: 16px;
}
.fluxo-list .table {
  --bs-table-bg: transparent; --bs-table-color: var(--portal-text);
  --bs-table-hover-bg: var(--portal-surface-soft); --bs-table-hover-color: var(--portal-text);
  --bs-table-border-color: var(--portal-border);
}
.fluxo-list thead th {
  color: var(--portal-muted); font-size: .72rem; text-transform: uppercase;
  letter-spacing: .04em; border-bottom: 1px solid var(--portal-border);
}
.fluxo-list tbody td { border-color: var(--portal-border); }
`;

function formatarData(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminFluxos() {
  const { temPermissao } = useAuth();
  const podeEditar = temPermissao('admin:fluxos');

  const [fluxos, setFluxos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [editandoId, setEditandoId] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/atendimento/fluxos`);
      setFluxos(r.data.data || []);
      setErro('');
    } catch (e) {
      setErro('Erro ao carregar fluxos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const novoFluxo = async () => {
    const { value: form } = await Swal.fire({
      title: 'Novo fluxo',
      html: `
        <input id="sw-nome" class="swal2-input" placeholder="Nome do fluxo" maxlength="120">
        <input id="sw-desc" class="swal2-input" placeholder="Descricao (opcional)" maxlength="500">
        <label style="display:flex;align-items:center;gap:8px;justify-content:center;margin-top:8px;font-size:.9rem">
          <input id="sw-reuso" type="checkbox"> Reutilizavel (subfluxo)
        </label>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const nome = document.getElementById('sw-nome').value.trim();
        if (!nome) { Swal.showValidationMessage('Informe o nome.'); return false; }
        return {
          nome,
          descricao: document.getElementById('sw-desc').value.trim(),
          reutilizavel: document.getElementById('sw-reuso').checked,
        };
      },
    });
    if (!form) return;
    try {
      const r = await axios.post(`${API}/atendimento/fluxos`, form);
      setEditandoId(r.data.data.id);
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.message || 'Falha ao criar fluxo.', 'error');
    }
  };

  const editarMeta = async (f) => {
    const { value: form } = await Swal.fire({
      title: 'Editar fluxo',
      html: `
        <input id="sw-nome" class="swal2-input" placeholder="Nome" maxlength="120" value="${(f.nome || '').replace(/"/g, '&quot;')}">
        <input id="sw-desc" class="swal2-input" placeholder="Descricao" maxlength="500" value="${(f.descricao || '').replace(/"/g, '&quot;')}">
        <label style="display:flex;align-items:center;gap:8px;justify-content:center;margin-top:8px;font-size:.9rem">
          <input id="sw-reuso" type="checkbox" ${f.reutilizavel ? 'checked' : ''}> Reutilizavel (subfluxo)
        </label>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const nome = document.getElementById('sw-nome').value.trim();
        if (!nome) { Swal.showValidationMessage('Informe o nome.'); return false; }
        return {
          nome,
          descricao: document.getElementById('sw-desc').value.trim(),
          reutilizavel: document.getElementById('sw-reuso').checked,
        };
      },
    });
    if (!form) return;
    try {
      await axios.put(`${API}/atendimento/fluxos/${f.id}`, form);
      carregar();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.message || 'Falha ao salvar.', 'error');
    }
  };

  const excluir = async (f) => {
    const c = await Swal.fire({
      title: 'Excluir fluxo?',
      text: `"${f.nome}" sera removido. Canais que usam a versao publicada continuam ate serem reapontados.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    });
    if (!c.isConfirmed) return;
    try {
      await axios.delete(`${API}/atendimento/fluxos/${f.id}`);
      carregar();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.message || 'Falha ao excluir.', 'error');
    }
  };

  if (!podeEditar) {
    return <div className="alert alert-warning m-4">Voce nao tem permissao para gerenciar fluxos.</div>;
  }

  if (editandoId) {
    return (
      <FluxoEditor
        fluxoId={editandoId}
        onBack={() => { setEditandoId(null); carregar(); }}
      />
    );
  }

  return (
    <div className="container-fluid py-4 fluxo-list">
      <style>{FLUXO_LIST_CSS}</style>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="mb-1" style={{ color: 'var(--portal-text)' }}>Fluxos do Bot</h4>
          <p className="mb-0" style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
            Construtor de fluxos de atendimento atribuiveis por canal.
          </p>
        </div>
        <button type="button" className="btn fluxo-btn-primary" onClick={novoFluxo}>
          <i className="bi bi-plus-lg me-1" /> Novo fluxo
        </button>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      {loading ? (
        <div className="text-center py-5" style={{ color: 'var(--portal-muted)' }}>Carregando...</div>
      ) : fluxos.length === 0 ? (
        <div className="fluxo-empty text-center py-5">
          <i className="bi bi-diagram-3" style={{ fontSize: '2.2rem', color: 'var(--portal-blue-soft)' }} />
          <p className="mt-2 mb-0" style={{ color: 'var(--portal-muted)' }}>
            Nenhum fluxo ainda. Clique em <strong style={{ color: 'var(--portal-text)' }}>Novo fluxo</strong> para comecar.
          </p>
        </div>
      ) : (
        <div className="fluxo-card table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Publicada</th>
                <th>Rascunho</th>
                <th>Canais</th>
                <th>Atualizado</th>
                <th className="text-end">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {fluxos.map((f) => (
                <tr key={f.id}>
                  <td>
                    <div className="fw-semibold">{f.nome}</div>
                    {f.descricao && <small className="text-muted">{f.descricao}</small>}
                  </td>
                  <td>
                    {f.reutilizavel
                      ? <span className="badge bg-info-subtle text-info-emphasis">Subfluxo</span>
                      : <span className="badge bg-primary-subtle text-primary-emphasis">Principal</span>}
                  </td>
                  <td>{f.versao_publicada ? `v${f.versao_publicada}` : <span className="text-muted">—</span>}</td>
                  <td>{f.tem_rascunho ? <span className="badge bg-warning-subtle text-warning-emphasis">sim</span> : <span className="text-muted">—</span>}</td>
                  <td>{f.canais_usando || 0}</td>
                  <td><small>{formatarData(f.updated_at)}</small></td>
                  <td className="text-end">
                    <button type="button" className="btn btn-sm btn-outline-primary me-1" onClick={() => setEditandoId(f.id)}>
                      <i className="bi bi-pencil-square me-1" /> Editar fluxo
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => editarMeta(f)}>
                      <i className="bi bi-info-circle" />
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => excluir(f)}>
                      <i className="bi bi-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
